import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { motion } from "framer-motion";
import { FiCheckCircle, FiEye, FiEyeOff, FiLock, FiWifiOff } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { API_BASE } from "../config/api";

const validationSchema = Yup.object({
  password: Yup.string()
    .min(8, "Minimum 8 characters")
    .matches(/[A-Z]/, "Include an uppercase letter")
    .matches(/[0-9]/, "Include a number")
    .required("Required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords do not match")
    .required("Required"),
});

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() || "";
  const [loading, setLoading] = useState(false);
  const [offline, setOffline] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const abortRef = useRef(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  const handleSubmit = async (values, { setStatus }) => {
    setLoading(true);
    setOffline(false);
    setStatus("");

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      await axios.post(
        `${API_BASE}/auth/reset-password`,
        { token, newPassword: values.password },
        { signal: abortRef.current.signal, timeout: 30000 }
      );

      setCompleted(true);
      toast.success("Your password has been reset.");
    } catch (error) {
      if (axios.isCancel(error)) return;

      if (error.code === "ECONNABORTED") {
        setOffline(true);
        setStatus("Request timed out. Please try again.");
      } else if (error.message === "Network Error") {
        setOffline(true);
        setStatus("Network error. Check your connection and try again.");
      } else {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "This reset link is invalid or has expired.";
        setStatus(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-100 via-sky-100 to-white p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl p-8 w-full max-w-md space-y-6"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100, damping: 14 }}
      >
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-blue-800">
            Create New Password
          </h1>
          <p className="text-slate-600 mt-1">
            Use at least 8 characters, one uppercase letter, and one number.
          </p>
        </div>

        {offline && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-800">
            <FiWifiOff />
            <span>We couldn&apos;t reach the server. Check your connection and try again.</span>
          </div>
        )}

        {!token ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
            This password-reset link is incomplete. Request a new link and try again.
          </div>
        ) : completed ? (
          <div className="space-y-5 text-center">
            <FiCheckCircle className="mx-auto text-5xl text-green-600" />
            <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-green-800">
              Your password has been updated. You can now sign in with your new password.
            </div>
            <Link
              to="/login"
              className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition"
            >
              Go to Login
            </Link>
          </div>
        ) : (
          <Formik
            initialValues={{ password: "", confirmPassword: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, status }) => (
              <Form className="space-y-5">
                {status && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {status}
                  </div>
                )}

                <PasswordField
                  name="password"
                  label="New password"
                  autoComplete="new-password"
                  showPassword={showPassword}
                  onToggle={() => setShowPassword((shown) => !shown)}
                />

                <PasswordField
                  name="confirmPassword"
                  label="Confirm new password"
                  autoComplete="new-password"
                  showPassword={showPassword}
                  onToggle={() => setShowPassword((shown) => !shown)}
                />

                <motion.button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition flex items-center justify-center"
                  whileTap={{ scale: 0.98 }}
                  disabled={loading || isSubmitting}
                >
                  {loading ? (
                    <motion.div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Reset Password"
                  )}
                </motion.button>
              </Form>
            )}
          </Formik>
        )}

        <div className="text-center text-sm">
          <Link className="text-blue-700 hover:underline" to="/login">
            Back to Login
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
}

function PasswordField({ name, label, autoComplete, showPassword, onToggle }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor={name}>
        {label}
      </label>
      <div className="relative">
        <FiLock className="absolute top-3.5 left-3 text-gray-400" />
        <Field
          id={name}
          name={name}
          type={showPassword ? "text" : "password"}
          autoComplete={autoComplete}
          className="pl-10 pr-10 py-3 w-full rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
        />
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          aria-label={showPassword ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          onClick={onToggle}
        >
          {showPassword ? <FiEyeOff /> : <FiEye />}
        </button>
      </div>
      <ErrorMessage name={name} component="div" className="text-red-500 text-sm ml-1 mt-1" />
    </div>
  );
}
